<template lang="pug">
.KeyboardInput(
  :class="{'KeyboardInput--hidden': hidden }"
  @keydown="handleKeyDown($event)"
  @keyup="handleKeyUp($event)"
  tabindex="0"
  ref="self"
)
  .debug(v-if="constants.IS_DEBUG")
    | (KeyboardInputDebugInfo) isTextTest: {{ isTextTest }} / isSingleKeyTest: {{ isSingleKeyTest }} / PreText: {{ preText }} / inputRomajiText: {{ inputRomajiText }} / currentRepeatCount: {{ currentRepeatCount }} / nextKeyCharacter {{ nextKeyCharacter }} / targetRomajiText: {{ targetRomajiText }} / correctCharacterIndex {{ correctCharacterIndex }}
  .targetText(
    v-if="targetText"
    ref="targetTextDOM"
  )
    .characterGroup(
      v-for="targetCharacterGroup in targetCharacterCollection"
      :class=`{
        block: characterGroupStyle === 'block'
      }`
    )
      .character(
        v-for="targetCharacter in targetCharacterGroup"
        :class=`{
          matched: targetCharacter.value === inputCharacters[targetCharacter.index] && targetCharacter.index <= correctCharacterIndex,
          current: targetCharacter.index === correctCharacterIndex
        }`
        :ref="targetCharacter.index === correctCharacterIndex ? 'currentTargetTextCharacterDOM' : 'targetTextCharacterDOM'"
      ) {{targetCharacter.value}}

  .inputText(
    ref="inputTextDOM"
  )
    .characterGroup(
      v-for="inputCharacterGroup in inputCharacterCollection"
      v-if="inputCharacterGroup.length"
      :class=`{
        block: characterGroupStyle === 'block'
      }`
    )
      .character(
        v-for="inputCharacter in inputCharacterGroup"
        ref="inputTextCharacterDOM"
      )
        | {{inputCharacter.value}}
    .caret(ref="inputTextCaretDOM") |


  .hand(
    v-if="!isHandHidden"
    :class="handClass"
    class="slow"
  )
    // .left(:class="{ top: leftHand.y === 1, middle: leftHand.y === 2, bottom: leftHand.y === 3, extend: leftHand.x === 6}")
    .left
      .noFinger(v-show="leftHand.x === 0"): img(src="@/assets/hand.svg")
      .finger1(v-show="leftHand.x === 1"): img(src="@/assets/hand_1_on.svg")
      .finger2(v-show="leftHand.x === 2"): img(src="@/assets/hand_2_on.svg")
      .finger3(v-show="leftHand.x === 3"): img(src="@/assets/hand_3_on.svg")
      .finger4(v-show="leftHand.x === 4"): img(src="@/assets/hand_4_on.svg")
      .finger4(v-show="leftHand.x === 6"): img(src="@/assets/hand_6_on.svg")

    //- .right(:class="{ top: rightHand.y === 1, middle: rightHand.y === 2, bottom: rightHand.y === 3, extend: rightHand.x === -1 }")
    .right
      .noFinger(v-show="rightHand.x === 0"): img(src="@/assets/hand.svg")
      .finger1(v-show="rightHand.x === 4"): img(src="@/assets/hand_1_on.svg")
      .finger2(v-show="rightHand.x === 3"): img(src="@/assets/hand_2_on.svg")
      .finger3(v-show="rightHand.x === 2"): img(src="@/assets/hand_3_on.svg")
      .finger4(v-show="rightHand.x === 1"): img(src="@/assets/hand_4_on.svg")
      .finger4(v-show="rightHand.x === -1"): img(src="@/assets/hand_6_on.svg")

  .keyboard
    .topLine
      .key(
        v-for="key, i in keys"
        v-if="key.y == 1"
        :style="{ 'grid-column': key.x }"
        :class="{ 'current': key.code === currentKey.code, 'current--correct': key.code === currentKey.code && currentKey.isCorrected, 'target': (isSingleKeyTest || targetColorEnabled) && key.code === internalTargetKeyCode }"
      )
        .inner
          .label {{ isHomePositionVisibled === true ? key.subLabel: '' }}
          //- .label {{ isHomePositionVisibled ? key.subLabel || key.label : key.label }}

    .middleLine
      .key(
        v-for="key, i in keys"
        v-if="key.y == 2"
        :style="{ 'grid-column': key.x }"
        :class="{ 'current': key.code === currentKey.code, 'current--correct': key.code === currentKey.code && currentKey.isCorrected, 'target': (isSingleKeyTest || targetColorEnabled) && key.code === internalTargetKeyCode }"
      )
        .inner
          .label {{ isHomePositionVisibled === true ? key.subLabel: '' }}
          //- .label {{ isHomePositionVisibled ? key.subLabel || key.label : key.label }}

    .bottomLine
      .key(
        v-for="key, i in keys"
        v-if="key.y == 3"
        :style="{ 'grid-column': key.x }"
        :class="{ 'current': key.code === currentKey.code, 'current--correct': key.code === currentKey.code && currentKey.isCorrected, 'target': (isSingleKeyTest || targetColorEnabled) && key.code === internalTargetKeyCode }"
      )
        .inner
          .label {{ isHomePositionVisibled === true ? key.subLabel : '' }}
          //- .label {{ isHomePositionVisibled ? key.subLabel || key.label : key.label }}


  .goodMessage(:class="{ hidden: !isRepeatFinished }") Good!
  .clearMessage(:class="{ hidden: !isFinished }") NICE!

</template>

<script>
import * as _ from 'lodash'
import * as constants from '@/constants'
import soundService from '@/services/SoundService'
import inputService from '@/services/InputService'

/**
 * 主な動作の流れ
 * マウント後、常にフォーカスがコンポーネントに当たるようにし入力内容を監視する
 * propsでtargetTextを受け取り表示する
 * 入力があった場合、仮入力テキストとして保存する
 * 仮入力が変換テーブルに入力内容と一致する文字列がないか確認する
 * 一致していたらinputTextにひらがな、inputRomajiTextにローマ字テキストを追加し、仮入力をクリアする
 * targetTextとinputTextが一致しているか確認する、一致していたらisFinishedをtrueにする
 * isFinished:trueの場合でもtestRepeatCountが設定されている場合は任意の回数繰り返す
 * 完了後はonInputFinishedをemitする
 */
export default {
  name: 'km-keyboard-input',
  components: {},
  props: {
    hidden: {
      type: Boolean
    },
    sceneId: {
      type: String
    },
    characterGroupStyle: {
      type: String,
      default: 'inline' // or 'block'
    },
    handType: {
      type: String,
      default: 'guide' // or 'simpleGuide' or 'response'
    },
    isHandHidden: {
      type: Boolean,
      default: false
    },
    isHomePositionVisibled: {
      type: Boolean,
      default: false
    },
    targetText: {
      default: () => []
    },
    testRepeatCount: {
      type: Number,
      default: 1
    },
    targetKey: {
      type: String
    },
    targetColorEnabled: {
      type: Boolean,
      default: true
    }
  },
  filters: {},
  data() {
    return {
      constants,
      isReady: false,
      isFinished: false,
      isRepeatFinished: false,
      preText: '', // 未変換テキスト(変換後に入力される)
      inputText: '', // 入力テキスト(かな)
      inputRomajiText: '', // 入力テキスト(ローマ字))
      currentRepeatCount: 0, // 現在の繰り返し回数(繰り返し有効時のみ使用)
      leftHand: { x: 0, y: 2 },
      rightHand: { x: 0, y: 2 },
      currentKey: {
        // 現在キーのデータ
        // character: 'f',
        // code: 'KeyF',
        // x: 4,
        // y: 2,
        // label: 'ふぁ',
        // subLabel: '_'
      },
      currentKeyResetTimer: null,
      internalTargetKeyCode: '', // singleKeyTest時はtargetKey、textTest時はnextKeyのkeyCodeが入る
      nextKeyCharacter: '', // textTest時の次キー
      keys: [
        { character: 'q', code: 'KeyQ', x: 1, y: 1 },
        { character: 'w', code: 'KeyW', x: 2, y: 1 },
        { character: 'e', code: 'KeyE', x: 3, y: 1, label: 'エ' },
        { character: 'r', code: 'KeyR', x: 4, y: 1 },
        { character: 't', code: 'KeyT', x: 6, y: 1 },
        { character: 'y', code: 'KeyY', x: 8, y: 1 },
        { character: 'u', code: 'KeyU', x: 10, y: 1, label: 'ウ' },
        { character: 'i', code: 'KeyI', x: 11, y: 1, label: 'イ' },
        { character: 'o', code: 'KeyO', x: 12, y: 1, label: 'オ' },
        { character: 'p', code: 'KeyP', x: 13, y: 1 },

        { character: 'a', code: 'KeyA', x: 1, y: 2, label: 'ア' },
        { character: 's', code: 'KeyS', x: 2, y: 2 },
        { character: 'd', code: 'KeyD', x: 3, y: 2 },
        {
          character: 'f',
          code: 'KeyF',
          x: 4,
          y: 2,
          label: 'ふぁ',
          subLabel: '_'
        },
        { character: 'g', code: 'KeyG', x: 6, y: 2, label: 'が' },
        { character: 'h', code: 'KeyH', x: 8, y: 2, label: 'は' },
        {
          character: 'j',
          code: 'KeyJ',
          x: 10,
          y: 2,
          label: 'じゃ',
          subLabel: '_'
        },
        { character: 'k', code: 'KeyK', x: 11, y: 2, label: 'か' },
        { character: 'l', code: 'KeyL', x: 12, y: 2 },
        { character: ';', code: 'Semicolon', x: 13, y: 2 },

        { character: 'z', code: 'KeyZ', x: 1, y: 3 },
        { character: 'x', code: 'KeyX', x: 2, y: 3 },
        { character: 'c', code: 'KeyC', x: 3, y: 3 },
        { character: 'v', code: 'KeyV', x: 4, y: 3 },
        { character: 'b', code: 'KeyB', x: 6, y: 3 },
        { character: 'n', code: 'KeyN', x: 8, y: 3 },
        { character: 'm', code: 'KeyM', x: 10, y: 3 },
        { character: ',', code: 'Comma', x: 11, y: 3 },
        { character: '.', code: 'Period', x: 12, y: 3 },
        { character: '/', code: 'Slash', x: 13, y: 3 }
      ]
    }
  },
  computed: {
    isSingleKeyTest: function() {
      return this.targetKey && this.targetKey.length !== 0
    },
    isTextTest: function() {
      return this.targetText && this.targetText.length !== 0
    },
    /**
     * 入力対象のローマ字テキストを作成
     */
    targetRomajiText: function() {
      if (!this.targetText) return ''
      return this.targetText.map(text =>
        inputService.getRomajiByHiraganaText(text)
      )
    },
    /**
     * 入力対象文字列から1文字ごとのコレクションを作成(1層目: Group / 2層目 Character)
     */
    targetCharacterCollection: function() {
      if (!this.targetText) return []
      // カウンターを使ってCollection全体でindexが割り振られるようにする
      let count = 0
      return this.targetText.map(text => {
        const characters = text.split('')
        return characters.map(character => {
          count += 1
          return {
            index: count - 1,
            value: character
          }
        })
      })
    },
    /**
     * 入力文字列から階層を持たない配列を作成(照合に使用)
     */
    inputCharacters: function() {
      return this.inputText.split('')
    },
    /**
     * 入力文字列から１文字ごとのコレクションを作成(入力対象の各配列要素の文字数で振り分ける)
     */
    inputCharacterCollection: function() {
      let headIndex = 0
      let count = 0
      const collection = []
      this.targetCharacterCollection.forEach(targetCharacterGroup => {
        const isReachTargetEnd =
          headIndex + targetCharacterGroup.length > this.inputText.length
        const inputGroupTempText = this.inputText.substring(
          headIndex,
          isReachTargetEnd
            ? this.inputText.length
            : headIndex + targetCharacterGroup.length
        )
        headIndex += inputGroupTempText.length
        collection.push(
          inputGroupTempText.split('').map(inputGroupCharacter => {
            count += 1
            return {
              index: count - 1,
              value: inputGroupCharacter
            }
          })
        )
      })

      // 溢れた分の対応
      const inputGroupTempText = this.inputText.substring(
        headIndex,
        this.inputText.length
      )
      collection.push(
        inputGroupTempText.split('').map(inputGroupCharacter => {
          count += 1
          return {
            index: count - 1,
            value: inputGroupCharacter
          }
        })
      )
      return collection
    },
    correctCharacterIndex: function() {
      let index = 0
      let isIncorrected = false
      this.targetCharacterCollection.forEach(targetCharacterGroup => {
        targetCharacterGroup.forEach(targetCharacter => {
          if (
            !isIncorrected &&
            targetCharacter.value ===
              this.inputCharacters[targetCharacter.index]
          ) {
            index++
          } else {
            isIncorrected = true
          }
        })
      })
      return index
    },
    handClass: function() {
      return {
        'hand--guide': this.handType === 'guide',
        'hand--simpleGuide': this.handType === 'simpleGuide',
        'hand--response': this.handType === 'response',
        'hand--leftTop': this.leftHand.y === 1 && this.leftHand.x !== 6,
        'hand--leftMiddle': this.leftHand.y === 2 && this.leftHand.x !== 6,
        'hand--leftBottom': this.leftHand.y === 3 && this.leftHand.x !== 6,
        'hand--leftTopExtend': this.leftHand.y === 1 && this.leftHand.x === 6,
        'hand--leftMiddleExtend':
          this.leftHand.y === 2 && this.leftHand.x === 6,
        'hand--leftBottomExtend':
          this.leftHand.y === 3 && this.leftHand.x === 6,
        'hand--rightTop': this.rightHand.y === 1 && this.rightHand.x !== -1,
        'hand--rightMiddle': this.rightHand.y === 2 && this.rightHand.x !== -1,
        'hand--rightBottom': this.rightHand.y === 3 && this.rightHand.x !== -1,
        'hand--rightTopExtend':
          this.rightHand.y === 1 && this.rightHand.x === -1,
        'hand--rightMiddleExtend':
          this.rightHand.y === 2 && this.rightHand.x === -1,
        'hand--rightBottomExtend':
          this.rightHand.y === 3 && this.rightHand.x === -1
      }
    }
  },
  watch: {
    sceneId: function() {
      this.setup()
    }
  },
  methods: {
    setup() {
      this.inputText = ''
      this.inputRomajiText = ''
      this.preText = ''
      this.currentRepeatCount = 0
      if (this.targetKey) this.setTargetKey()
      if (this.targetText) this.setNextKey()
      this.isFinished = false
      this.isReady = true
    },
    repeat() {
      this.isFinished = false
      this.isRepeatFinished = true
      setTimeout(() => {
        this.inputText = ''
        this.inputRomajiText = ''
        this.preText = ''
        if (this.targetKey) this.setTargetKey()
        if (this.targetText) this.setNextKey()
        this.isRepeatFinished = false
      }, 500)
    },
    focus() {
      this.$refs.self.focus()
    },
    /**
     * 現在の入力キーを更新(ハイライト制御)
     */
    updateCurrentKey(key, isCorrected) {
      clearTimeout(this.currentKeyResetTimer)
      this.currentKeyResetTimer = null
      this.currentKey = Object.assign({}, key, { isCorrected })
    },
    /**
     * 現在の入力キーを削除(ハイライト制御)
     */
    removeCurrentKey() {
      this.currentKeyResetTimer = setTimeout(() => {
        this.currentKey = {}
      }, 400)
    },
    /**
     * 手の位置を更新
     */
    updateHand(key) {
      this.leftHand.x = 0
      this.rightHand.x = 0
      const isLeftHand = key.x <= 6
      if (isLeftHand) {
        this.rightHand.y = 2
        this.leftHand.x = key.x
        this.leftHand.y = key.y
      }
      const isRightHand = key.x >= 8
      if (isRightHand) {
        this.leftHand.y = 2
        this.rightHand.x = key.x - 9
        this.rightHand.y = key.y
      }
    },
    /**
     * 入力文字列の追加
     */
    async addText(key) {
      const isCorrect =
        this.targetRomajiText
          .join('')
          .indexOf(`${this.inputRomajiText}${key}`) === 0
      if (!isCorrect) {
        this.inputText = this.inputText.replace(/＊/g, '')
        this.inputRomajiText = this.preText.length
          ? this.inputRomajiText.slice(0, -this.preText.length)
          : this.inputRomajiText
        this.preText = ''
        return false
      }
      this.inputText += '＊'
      this.inputRomajiText += key
      this.preText += key
      return true
    },
    /**
     * 仮入力分の変換を試みる
     */
    convertPreText() {
      if (
        this.isSingleKeyTest ||
        inputService.isConvertable(this.preText) === false
      ) {
        return
      }
      const matchedPattern = inputService.getPatternByRomaji(this.preText)
      // inputTextから*を削り、変換した文字列を追加する
      this.inputText =
        this.inputText.slice(0, -matchedPattern.input.length) +
        matchedPattern.output
      // 仮入力をクリアする
      this.preText = ''
    },
    playHitSound(isCorrected, keyY, isVowel) {
      if (isCorrected) {
        if (isVowel) {
          soundService.play('vowel')
          return
        }
        switch (keyY) {
          case 1:
            soundService.play('keyUpper')
            break
          case 2:
            soundService.play('key')
            break
          case 3:
            soundService.play('keyLower')
            break
        }
      } else {
        soundService.play('fail')
      }
    },
    finish(isCorrected) {
      const targetRomajiText = this.targetRomajiText.join('')
      this.isFinished =
        (this.isSingleKeyTest && isCorrected) ||
        (this.isTextTest && targetRomajiText === this.inputRomajiText)
      if (!this.isFinished) return false
      this.currentRepeatCount += 1
      const isRepeatRequired = this.currentRepeatCount < this.testRepeatCount
      if (isRepeatRequired) {
        this.repeat()
        return
      }
      soundService.play('goal')
      this.$emit('onInputFinished', this.inputText)
    },
    /**
     * 合否判定
     */
    checkAnswer(key, event, isAdded) {
      return (
        (this.isSingleKeyTest && this.targetKey === event.code) ||
        (this.isTextTest && isAdded)
      )
    },
    setTargetKey() {
      this.internalTargetKeyCode = this.targetKey
      const key = _.find(this.keys, { code: this.targetKey })
      this.updateHand(key)
    },
    setNextKey() {
      const targetRomajiText = this.targetRomajiText.join('')
      this.nextKeyCharacter = targetRomajiText.slice(
        this.inputRomajiText.length,
        this.inputRomajiText.length + 1
      )
      const nextKeyData = _.find(this.keys, {
        character: this.nextKeyCharacter
      })
      if (!nextKeyData) return
      if (this.handType === 'guide' || this.handType === 'simpleGuide') {
        this.updateHand(nextKeyData)
      }
      this.internalTargetKeyCode = nextKeyData ? nextKeyData.code : null
    },
    /**
     * キーを押した時の処理
     */
    async handleKeyDown(event) {
      const isSkip = event.code === 'Space'
      if ((!this.isReady && !isSkip) || (this.isFinished && !isSkip)) {
        return
      }
      const inputKey = inputService.getKeyDetail(event.code)
      const isAcceptedKey = !_.isEmpty(inputKey)

      if (isAcceptedKey) {
        const isAdded = await this.addText(event.key.toLowerCase())
        if (this.isTextTest) this.setNextKey()
        this.convertPreText()
        const isCorrected = this.checkAnswer(inputKey, event, isAdded)
        this.playHitSound(
          isCorrected,
          inputKey.y,
          inputService.isVowel(event.code)
        )
        this.updateCurrentKey(inputKey, isCorrected)
        if (this.handType === 'response') {
          this.updateHand(inputKey)
        }
        this.finish(isCorrected)
      } else {
        if (event.code === 'Space') {
          this.$emit('onSpaceKeyDown') // skip
        }
        this.$emit('onInput', this.inputText, this.correctCharacterIndex)
      }
    },
    handleKeyUp() {
      this.removeCurrentKey()
    }
  },
  mounted() {
    this.$refs.self.focus()
    this.$refs.self.addEventListener('blur', this.focus)
  },
  beforeDestroy() {
    this.$refs.self.removeEventListener('blur', this.focus)
  }
}
</script>

<style lang="scss" scoped>
.debug {
  position: fixed;
  top: 0;
  left: 0;
  color: #fff;
}

.KeyboardInput {
  position: relative;
  margin: 0 auto;
  padding: 25px 0;
  width: 800px;
  height: 460px;
  border: 4px solid #000;
  background: #fff;
  text-align: center;
  border-radius: 20px;
  overflow: hidden;
  &:focus {
    outline: none;
  }
  &--hidden {
    padding: 0;
    width: 0;
    height: 0;
    border: 0;
  }
}

.characterGroup {
  display: inline;
  margin: 0 10px;
  vertical-align: top;
  line-height: 45px;

  &.block {
    display: block;
  }
}

.character {
  display: inline-block;
  height: 45px;

  &.current {
    background: orange;
  }

  &.matched {
    color: orange;
  }
}

.caret {
  display: inline-block;
  margin: 0 10px;
  height: 45px;
  vertical-align: top;
  animation-name: blink;
  animation-duration: 1.1s;
  animation-iteration-count: infinite;
  animation-timing-function: step-end;
}

.targetText {
  margin: 0 40px 30px;
  min-height: 45px;
  max-height: 90px;
  border-bottom: 1px solid #000;
  background: rgba(255, 255, 255, 0.5);
  font-size: 3rem;
  text-align: left;
  line-height: 45px;
  overflow: hidden;
}

.inputText {
  margin: 0 40px 30px;
  height: 45px;
  border-bottom: 1px solid #000;
  background: #fff;
  font-size: 3rem;
  line-height: 45px;
  text-align: left;
  overflow: hidden;
}

.keyboard {
  display: inline-block;
  margin: 0 auto;

  .topLine,
  .middleLine,
  .bottomLine {
    display: grid;
    width: calc((50px * 10) + 0);
    height: 50px;
    grid-template-columns: repeat(4, 50px) 0px 50px 0px 50px 0px repeat(4, 50px);
    grid-template-rows: repeat(3, 50px);
  }

  .middleLine {
    margin-left: 10px;
  }

  .bottomLine {
    margin-left: 20px;
  }
}

.key {
  // width: 50px;

  .inner {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 2px;
    height: calc(100% - 4px);
    background: #ddd;
    border-radius: 5px;
    transition-property: background, transform;
    transition-timing-function: linear;
    transition-duration: 0.25s;
  }
  &.target .inner {
    color: #fff;
    animation: 0.5s linear hilight infinite alternate;
  }
  &.current .inner {
    background: $keyColor;
    color: #fff;
    transform: scale(0.9);
  }
  &.current--correct .inner {
    background: orange;
    color: #fff;
    transform: scale(0.9);
  }
}

.clearMessage,
.goodMessage {
  position: fixed;
  top: calc(50% - 100px);
  left: calc(50% - 150px);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 300px;
  height: 150px;
  border: 10px solid orange;
  color: orange;
  font-size: 60px;
  font-weight: bold;
  opacity: 1;
  transform: scale(1.5) rotate(-20deg);
  transition-property: transform, opacity;
  z-index: 60;
  pointer-events: none;

  &.hidden {
    transform: scale(0.5);
    opacity: 0;
  }
}

.hand {
  position: relative;
  z-index: 50;

  &.slow {
    .left,
    .right {
      transition: all 0.5s ease;
    }
  }

  .left,
  .right {
    position: absolute;
    transition: opacity 0.1s ease;
    opacity: 0;

    .noFinger {
      opacity: 1;
      animation: 0.3s ease fadeOut forwards;
      @keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0.5;
        }
      }
    }

    img {
      position: absolute;
      width: 245px;
    }
  }
  .left {
    top: 50px;
    left: 113px;
  }

  .right {
    top: 50px;
    right: 150px;
    transform: rotateY(180deg);
  }
}

// lefthand(移動あり)
$leftHandTypes: (
  leftTop: (
    0px,
    140px
  ),
  leftBottom: (
    100px,
    160px
  ),
  leftTopExtend: (
    0px,
    144px
  ),
  leftBottomExtend: (
    100px,
    164px
  )
);
@each $handTypeName, $value in $leftHandTypes {
  $i: index($leftHandTypes, $handTypeName);
  .hand--simpleGuide.hand--#{$handTypeName},
  .hand--response.hand--#{$handTypeName} {
    .left {
      top: nth($value, 1);
      left: nth($value, 2);
      opacity: 1;
    }
  }
  .hand--guide.hand--#{$handTypeName} {
    .left {
      top: 50px;
      left: if(
        $handTypeName == leftTopExtend or $handTypeName == leftBottomExtend,
        154px,
        150px
      );
      opacity: 1;
      animation: 1.8s ease #{$handTypeName} infinite forwards;
      animation-delay: 0s;
    }
    @keyframes #{$handTypeName} {
      0% {
        top: 50px;
        left: if(
          $handTypeName == leftTopExtend or $handTypeName == leftBottomExtend,
          154px,
          150px
        );
        opacity: 1;
      }
      20% {
        top: 50px;
        left: if(
          $handTypeName == leftTopExtend or $handTypeName == leftBottomExtend,
          154px,
          150px
        );
        opacity: 1;
      }
      70% {
        top: nth($value, 1);
        left: nth($value, 2);
        opacity: 1;
      }
      100% {
        top: nth($value, 1);
        left: nth($value, 2);
        opacity: 0;
      }
    }
  }
}

// lefthand(移動なし)
$leftHandTypes: (
  leftMiddle: (
    50px,
    150px
  ),
  leftMiddleExtend: (
    50px,
    154px
  )
);
@each $handTypeName, $value in $leftHandTypes {
  $i: index($leftHandTypes, $handTypeName);
  .hand--simpleGuide.hand--#{$handTypeName},
  .hand--response.hand--#{$handTypeName} {
    .left {
      top: nth($value, 1);
      left: nth($value, 2);
      opacity: 1;
    }
  }
  .hand--guide.hand--#{$handTypeName} {
    .left {
      top: 50px;
      left: if($handTypeName == leftMiddleExtend, 154px, 150px);
      opacity: 1;
      animation: 1.8s ease #{$handTypeName} forwards;
      animation-delay: 0s;
    }
    @keyframes #{$handTypeName} {
      0% {
        top: 50px;
        left: if($handTypeName == leftMiddleExtend, 154px, 150px);
        opacity: 1;
      }
      20% {
        top: 50px;
        left: if($handTypeName == leftMiddleExtend, 154px, 150px);
        opacity: 1;
      }
      100% {
        top: nth($value, 1);
        left: nth($value, 2);
        opacity: 1;
      }
    }
  }
}

// righthand(移動あり)
$rightHandTypes: (
  rightTop: (
    0px,
    160px
  ),
  rightBottom: (
    100px,
    140px
  ),
  rightTopExtend: (
    0px,
    164px
  ),
  rightBottomExtend: (
    100px,
    144px
  )
);

@each $handTypeName, $value in $rightHandTypes {
  $i: index($rightHandTypes, $handTypeName);
  .hand--simpleGuide.hand--#{$handTypeName},
  .hand--response.hand--#{$handTypeName} {
    .right {
      top: nth($value, 1);
      right: nth($value, 2);
      opacity: 1;
    }
  }
  .hand--guide.hand--#{$handTypeName} {
    .right {
      top: 50px;
      right: if(
        $handTypeName == rightTopExtend or $handTypeName == rightBottomExtend,
        154px,
        150px
      );
      opacity: 1;
      animation: 1.8s ease #{$handTypeName} infinite forwards;
      animation-delay: 0s;
    }
    @keyframes #{$handTypeName} {
      0% {
        top: 50px;
        right: if(
          $handTypeName == rightTopExtend or $handTypeName == rightBottomExtend,
          154px,
          150px
        );
        opacity: 1;
      }
      20% {
        top: 50px;
        right: if(
          $handTypeName == rightTopExtend or $handTypeName == rightBottomExtend,
          154px,
          150px
        );
        opacity: 1;
      }
      70% {
        top: nth($value, 1);
        right: nth($value, 2);
        opacity: 1;
      }
      100% {
        top: nth($value, 1);
        right: nth($value, 2);
        opacity: 0;
      }
    }
  }
}

// righthand(移動なし)
$rightHandTypes: (
  rightMiddle: (
    50px,
    150px
  ),
  rightMiddleExtend: (
    50px,
    154px
  )
);
@each $handTypeName, $value in $rightHandTypes {
  $i: index($rightHandTypes, $handTypeName);
  .hand--simpleGuide.hand--#{$handTypeName},
  .hand--response.hand--#{$handTypeName} {
    .right {
      top: nth($value, 1);
      right: nth($value, 2);
      opacity: 1;
    }
  }
  .hand--guide.hand--#{$handTypeName} {
    .right {
      top: 50px;
      right: if($handTypeName == rightMiddleExtend, 154px, 150px);
      opacity: 1;
      animation: 1.8s ease #{$handTypeName} forwards;
      animation-delay: 0s;
    }
    @keyframes #{$handTypeName} {
      0% {
        top: 50px;
        right: if($handTypeName == rightMiddleExtend, 154px, 150px);
        opacity: 1;
      }
      20% {
        top: 50px;
        right: if($handTypeName == rightMiddleExtend, 154px, 150px);
        opacity: 1;
      }
      100% {
        top: nth($value, 1);
        right: nth($value, 2);
        opacity: 1;
      }
    }
  }
}

@keyframes blink {
  from,
  to {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes hilight {
  from {
    background: #ddd;
  }
  to {
    background: orange;
  }
}
</style>
